import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError, fileUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { MODES, modeColor, soberLabel, relationshipLabel, COMUNAS_RM } from "@/constants/comunas";
import { likeVerbForCountry, COUNTRIES } from "@/constants/countries";
import { motion, AnimatePresence, useMotionValue, useTransform, animate as fmAnimate } from "framer-motion";
import { toast } from "sonner";
import { Sparkles, X, MapPin, Heart, SlidersHorizontal, HeartHandshake, Smile, Sprout, PencilLine, Ruler, Baby, Star, Check } from "lucide-react";
import Avatar from "@/components/Avatar";
import { Icon, iconForActivity } from "@/lib/icons";

// Mode icon renderer to match the design system tokens
const MODE_ICONS = { apoyo: HeartHandshake, amistad: Smile, amor: Heart };

export default function Discover() {
  const { user } = useAuth();
  const nav = useNavigate();
  const userModes = user?.modes || [];
  const swipeModes = MODES.filter((m) => m.v !== "grupos" && userModes.includes(m.v));
  const [mode, setMode] = useState(swipeModes[0]?.v || "amistad");
  const [candidates, setCandidates] = useState([]);
  const [idx, setIdx] = useState(0);
  const [quota, setQuota] = useState({ remaining: 20 });
  const [activities, setActivities] = useState([]);
  const [planModal, setPlanModal] = useState(null); // {profile}
  const [matchModal, setMatchModal] = useState(null); // {other, activity}
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);
  // Filtros persistidos en localStorage — se restauran al montar y se guardan
  // cada vez que el usuario aplica cambios. `countries` es una lista ISO-2.
  const DEFAULT_FILTERS = { age_min: "", age_max: "", comuna: "", radius_km: "", scope: "country", countries: [] };
  const FILTERS_LS_KEY = "ps.discover.filters.v1";
  const [filters, setFilters] = useState(() => {
    try {
      const raw = localStorage.getItem(FILTERS_LS_KEY);
      if (!raw) return DEFAULT_FILTERS;
      const parsed = JSON.parse(raw);
      return { ...DEFAULT_FILTERS, ...parsed, countries: Array.isArray(parsed.countries) ? parsed.countries : [] };
    } catch { return DEFAULT_FILTERS; }
  });

  useEffect(() => { api.get("/activities").then((r)=>setActivities(r.data)); }, []);

  // Ref used by ProfileCard to receive the "return card" command when modal closes without submitting
  const cardResetRef = useRef(null);

  const load = async (m) => {
    setLoading(true);
    setIdx(0);
    try {
      const params = { mode: m };
      if (filters.age_min) params.age_min = Number(filters.age_min);
      if (filters.age_max) params.age_max = Number(filters.age_max);
      if (filters.comuna) params.comuna = filters.comuna;
      if (filters.radius_km) params.radius_km = Number(filters.radius_km);
      if (filters.scope) params.scope = filters.scope;
      if (filters.scope === "custom" && filters.countries?.length) {
        params.countries = filters.countries.join(",");
      }
      const [{ data: cands }, { data: q }] = await Promise.all([
        api.get(`/discover`, { params }),
        api.get(`/discover/quota`),
      ]);
      setCandidates(cands);
      setQuota(q);
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (mode) load(mode); /* eslint-disable-next-line */ }, [mode]);

  const applyFilters = () => {
    setFiltersOpen(false);
    try { localStorage.setItem(FILTERS_LS_KEY, JSON.stringify(filters)); } catch {}
    load(mode);
  };
  const clearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setFiltersOpen(false);
    try { localStorage.removeItem(FILTERS_LS_KEY); } catch {}
    setTimeout(() => load(mode), 50);
  };
  const activeFilterCount = (filters.age_min ? 1 : 0) + (filters.age_max ? 1 : 0) + (filters.comuna ? 1 : 0) + (filters.radius_km ? 1 : 0) + (filters.scope !== "country" ? 1 : 0);

  const toggleCountry = (code) => {
    setFilters((f) => {
      const cs = new Set(f.countries || []);
      if (cs.has(code)) cs.delete(code); else cs.add(code);
      return { ...f, countries: Array.from(cs) };
    });
  };

  const current = candidates[idx];
  const nextProfile = candidates[idx + 1];
  // Microcopy localizado por país: MX "me late", AR "me copa", CO "me suena",
  // PE "me provoca", CL "me tinca", resto neutro "me gusta el plan".
  const userCountry = (user?.country || user?.location?.country || "CL").toUpperCase();
  const likeVerb = likeVerbForCountry(userCountry);
  const likeVerbCap = likeVerb.charAt(0).toUpperCase() + likeVerb.slice(1);

  const pass = async () => {
    if (!current) return;
    try { await api.post("/pass", { target_user_id: current.id, mode }); } catch (_e) { /* ignore */ }
    setIdx((i) => i + 1);
  };

  const openLike = () => { if (current) setPlanModal({ profile: current }); };

  // Called when the plan modal closes WITHOUT a like being sent (user cancelled).
  // We revert the card to the center so the user can try again and don't burn a slot.
  const cancelPlan = () => {
    setPlanModal(null);
    cardResetRef.current?.();
  };

  const sendLike = async (activity_id, no_plan) => {
    if (!current) return;
    try {
      const { data } = await api.post("/like", { target_user_id: current.id, mode, activity_id: activity_id || null, no_plan });
      setPlanModal(null);
      setQuota((q) => ({ ...q, remaining: Math.max(0, q.remaining - 1) }));
      if (data.match) {
        setMatchModal({ other: data.other, activity: data.proposed_activity, match_id: data.match_id });
      }
      setIdx((i) => i + 1);
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail) || ex.message);
      setPlanModal(null);
    }
  };

  // Suggested activities: user's favs intersected with candidate's favs.
  // "Chatear online" (is_virtual) is ALWAYS surfaced first as a low-friction option.
  const suggestedActivities = () => {
    const virtual = activities.filter((a) => a.is_virtual);
    const real = activities.filter((a) => !a.is_virtual);
    if (!current || !user) return [...virtual, ...real.slice(0, 2)];
    const mine = new Set(user.favorite_activities || []);
    const theirs = new Set(current.favorite_activities || []);
    const common = real.filter((a) => mine.has(a.id) && theirs.has(a.id));
    const picks = common.length >= 2
      ? common.slice(0, 2)
      : [...common, ...real.filter((a) => !common.find((c) => c.id === a.id))].slice(0, 2);
    return [...virtual, ...picks];
  };

  // Keyboard shortcuts for desktop (← pass, → like)
  useEffect(() => {
    const onKey = (e) => {
      if (!current || planModal || matchModal || filtersOpen) return;
      const target = e.target;
      const tag = target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || target?.isContentEditable) return;
      if (e.key === "ArrowLeft") { e.preventDefault(); pass(); }
      else if (e.key === "ArrowRight") { e.preventDefault(); openLike(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line
  }, [current, planModal, matchModal, filtersOpen, idx]);

  if (!swipeModes.length) {
    return (
      <div className="mx-auto max-w-md px-6 pt-12">
        <h1 className="font-display text-3xl font-black">Activa un modo</h1>
        <p className="mt-3 text-white/60">En tu perfil, activa Apoyo, Amistad o Amor para empezar a descubrir gente.</p>
        <button onClick={()=>nav("/app/perfil/editar")} className="ps-btn-primary mt-6">Editar perfil</button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md px-4 pt-6">
      {/* Mode selector */}
      <div className="flex items-center justify-between mb-4 px-2">
        <h1 className="font-display text-2xl font-black">Descubrir</h1>
        <span data-testid="quota-remaining" className="text-xs text-white/50">{quota.remaining}/20 {likeVerb}</span>
      </div>
      <div className="flex gap-2 mb-5 overflow-x-auto pb-1 items-center">
        {swipeModes.map((m) => {
          const M = MODE_ICONS[m.v] || Sparkles;
          const active = mode === m.v;
          return (
            <button key={m.v} data-testid={`mode-${m.v}`} onClick={()=>setMode(m.v)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border transition flex-shrink-0 ${
                active ? "border-transparent ps-gradient text-white" : "bg-white/5 border-white/[.16] text-[#C7CBD6]"
              }`}>
              <M size={13} strokeWidth={1.9}/> {m.l}
            </button>
          );
        })}
        <button data-testid="open-filters" onClick={()=>setFiltersOpen(true)}
          className={`ml-auto flex-shrink-0 relative inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border transition ${
            activeFilterCount>0 ? "border-transparent ps-gradient text-white" : "bg-white/5 border-white/[.16] text-[#C7CBD6]"
          }`}>
          <SlidersHorizontal size={13} strokeWidth={1.9}/> Filtros
          {activeFilterCount > 0 && <span className="ml-1 min-w-[16px] h-[16px] px-1 rounded-full bg-white/25 text-white text-[10px] font-bold flex items-center justify-center">{activeFilterCount}</span>}
        </button>
      </div>

      {/* Filters modal */}
      <AnimatePresence>
        {filtersOpen && (
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-4">
            <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
              className="ps-card w-full max-w-md p-6 relative">
              <button onClick={()=>setFiltersOpen(false)} className="absolute top-4 right-4 text-white/50"><X size={20}/></button>
              <h2 className="font-display text-2xl font-black">Filtros</h2>
              <p className="text-sm text-white/60 mt-1">Afina tu búsqueda.</p>
              <div className="mt-5 space-y-4">
                <div>
                  <label className="text-sm text-white/60 mb-2 block">Rango de edad</label>
                  <div className="grid grid-cols-2 gap-3">
                    <input data-testid="filter-age-min" type="number" min={18} max={99} placeholder="Desde" className="ps-input"
                      value={filters.age_min} onChange={(e)=>setFilters({...filters, age_min: e.target.value})}/>
                    <input data-testid="filter-age-max" type="number" min={18} max={99} placeholder="Hasta" className="ps-input"
                      value={filters.age_max} onChange={(e)=>setFilters({...filters, age_max: e.target.value})}/>
                  </div>
                </div>
                <div>
                  <label className="text-sm text-white/60 mb-2 block">Comuna</label>
                  <select data-testid="filter-comuna" className="ps-input" value={filters.comuna} onChange={(e)=>setFilters({...filters, comuna: e.target.value})}>
                    <option value="">Cualquiera</option>
                    {COMUNAS_RM.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-white/60 mb-2 block">Distancia máxima</label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { v: "", l: "Sin límite" },
                      { v: "5", l: "5 km" },
                      { v: "10", l: "10 km" },
                      { v: "25", l: "25 km" },
                      { v: "50", l: "50 km" },
                      { v: "100", l: "100 km" },
                    ].map((r) => (
                      <button key={r.l} type="button" data-testid={`filter-radius-${r.v || 'any'}`}
                        onClick={()=>setFilters({...filters, radius_km: r.v})}
                        className={`px-3 py-2 rounded-full text-xs font-semibold border transition ${filters.radius_km === r.v ? "border-transparent ps-gradient text-white" : "bg-white/5 border-white/10 text-white/70"}`}>
                        {r.l}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-sm text-white/60 mb-2 block">Alcance</label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { v: "nearby",   l: "Cerca de mí" },
                      { v: "country",  l: "Mi país" },
                      { v: "regional", l: "Toda Latinoamérica" },
                      { v: "custom",   l: "Elegir países" },
                    ].map((s) => (
                      <button key={s.v} type="button" data-testid={`filter-scope-${s.v}`}
                        onClick={()=>setFilters({...filters, scope: s.v})}
                        className={`px-3 py-2 rounded-full text-xs font-semibold border transition ${filters.scope === s.v ? "border-transparent ps-gradient text-white" : "bg-white/5 border-white/10 text-white/70"}`}>
                        {s.l}
                      </button>
                    ))}
                  </div>
                  <p className="mt-1.5 text-[11px] text-white/40">
                    {filters.scope === "custom"
                      ? "Elige uno o varios países. Si no marcas ninguno, se usará tu país."
                      : "Con \"Toda Latinoamérica\" verás gente de los 22 países hispanohablantes."}
                  </p>

                  {filters.scope === "custom" && (
                    <div className="mt-3" data-testid="filter-countries-picker">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[11px] uppercase tracking-wider text-white/50">
                          Países seleccionados · {filters.countries.length}
                        </span>
                        {filters.countries.length > 0 && (
                          <button
                            type="button"
                            data-testid="filter-countries-clear"
                            onClick={()=>setFilters({...filters, countries: []})}
                            className="text-[11px] text-white/60 hover:text-white/90 underline decoration-white/20"
                          >
                            Limpiar
                          </button>
                        )}
                      </div>
                      <div className="max-h-56 overflow-y-auto rounded-2xl bg-white/5 border border-white/10 p-2 grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                        {COUNTRIES.map((c) => {
                          const active = filters.countries.includes(c.code);
                          return (
                            <button
                              key={c.code}
                              type="button"
                              data-testid={`filter-country-${c.code}`}
                              onClick={()=>toggleCountry(c.code)}
                              className={`flex items-center justify-between gap-1.5 px-2.5 py-1.5 rounded-xl text-[12px] font-semibold border transition text-left ${active ? "border-transparent ps-gradient text-white" : "bg-white/[.03] border-white/10 text-white/75 hover:bg-white/[.06]"}`}
                            >
                              <span className="flex items-center gap-1.5 min-w-0">
                                <span className="text-base leading-none shrink-0">{c.flag}</span>
                                <span className="truncate">{c.name}</span>
                              </span>
                              {active && <Check size={14} strokeWidth={2.5} className="shrink-0"/>}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-6 flex gap-2">
                <button data-testid="filters-clear" onClick={clearFilters} className="ps-btn-secondary flex-1">Limpiar</button>
                <button data-testid="filters-apply" onClick={applyFilters} className="ps-btn-primary flex-1">Aplicar</button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {loading ? (
        <div className="ps-card h-[65vh] flex items-center justify-center"><div className="w-10 h-10 rounded-full border-4 border-white/10 border-t-[#FF6B5E] animate-spin"/></div>
      ) : !current ? (
        <div className="ps-card p-8 text-center">
          <Sprout size={48} strokeWidth={1.6} className="mx-auto text-[#8E93A3] mb-3"/>
          <p className="font-display text-xl font-bold">Por ahora no hay más personas en tu zona.</p>
          <p className="mt-2 text-[#C7CBD6] text-sm">La comunidad está creciendo. Mientras tanto, mira los grupos y eventos.</p>
          <button onClick={()=>nav("/app/grupos")} className="ps-btn-primary mt-6">Ver grupos</button>
        </div>
      ) : (
        <div className="relative">
          {/* Next card peek (behind) */}
          {nextProfile && (
            <div key={`next-${nextProfile.id}`} className="absolute inset-x-0 top-0 pointer-events-none" style={{ transform: "scale(0.95)", opacity: 0.55, filter: "blur(0.3px)" }}>
              <StaticProfilePreview profile={nextProfile}/>
            </div>
          )}
          <ProfileCard
            key={current.id}
            profile={current}
            mode={mode}
            activities={activities}
            onPass={pass}
            onLike={openLike}
            resetRef={cardResetRef}
            likeVerbCap={likeVerbCap}
          />
        </div>
      )}

      {/* Plan Modal */}
      <AnimatePresence>
        {planModal && (
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={(e)=>{ if (e.target === e.currentTarget) cancelPlan(); }}>
            <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
              className="ps-card w-full max-w-md p-6 relative">
              <button onClick={cancelPlan} className="absolute top-4 right-4 text-white/50"><X size={20}/></button>
              <h2 className="font-display text-2xl font-black">¿Qué plan harías con {planModal.profile.alias}?</h2>
              <p className="mt-2 text-white/60 text-sm">Elige una actividad para proponerle o deja que ella/él/elle proponga.</p>
              <div className="mt-4 space-y-2">
                {suggestedActivities().map((a) => (
                  <button key={a.id} data-testid={`suggest-${a.id}`} onClick={()=>sendLike(a.id, false)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl border text-left transition ${a.is_virtual ? "bg-[#4ADE80]/10 border-[#4ADE80]/40 hover:bg-[#4ADE80]/15" : "bg-white/5 border-white/[.16] hover:bg-white/10"}`}>
                    <span className="ps-icn"><Icon name={iconForActivity(a)} size={20}/></span>
                    <span className="font-semibold text-white flex-1">{a.name}</span>
                    {a.is_virtual && (
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ color: "#4ADE80", background: "rgba(74,222,128,.14)", border: "1px solid rgba(74,222,128,.42)" }}>
                        Sin compromiso
                      </span>
                    )}
                  </button>
                ))}
                <details className="w-full">
                  <summary className="cursor-pointer px-4 py-3 rounded-2xl bg-white/5 border border-white/[.16] text-sm font-semibold text-white">Otro plan…</summary>
                  <div className="mt-2 max-h-56 overflow-y-auto space-y-1">
                    {activities.filter((a) => !a.is_virtual).map((a) => (
                      <button key={a.id} onClick={()=>sendLike(a.id, false)} className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 text-left text-sm">
                        <Icon name={iconForActivity(a)} size={18}/> <span>{a.name}</span>
                      </button>
                    ))}
                  </div>
                </details>
                <button data-testid="like-no-plan" onClick={()=>sendLike(null, true)} className="w-full px-4 py-3 rounded-2xl text-sm text-[#C7CBD6] border border-dashed border-white/20 hover:bg-white/5">
                  Solo me interesa, sin plan aún
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Match Modal */}
      <AnimatePresence>
        {matchModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-md p-4">
            <motion.div initial={{ scale: 0.85, y: 20 }} animate={{ scale: 1, y: 0 }} className="relative w-full max-w-md">
              <div className="absolute -inset-8 ps-gradient rounded-[40px] opacity-30 blur-3xl"/>
              <div className="ps-card p-8 text-center relative">
                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.15, type: "spring" }}
                  className="mx-auto w-20 h-20 rounded-full ps-gradient flex items-center justify-center mb-4">
                  <Sparkles size={38}/>
                </motion.div>
                <h2 className="font-display text-4xl font-black">¡Hay plan!</h2>
                <p className="mt-2 text-white/70">A ti y a <span className="font-bold text-white">{matchModal.other?.alias}</span> les tinca juntarse.</p>
                {matchModal.activity && (
                  <div className="mt-4 ps-card p-3 border border-[#4ADE80]/30 inline-flex items-center gap-2">
                    <Icon name={iconForActivity(matchModal.activity)} size={22}/>
                    <div className="text-left">
                      <p className="text-[10px] text-white/50 uppercase tracking-[.13em] font-bold">Plan propuesto</p>
                      <p className="font-display text-lg font-bold mt-0.5">{matchModal.activity.name}</p>
                    </div>
                  </div>
                )}
                <div className="mt-6 flex gap-3">
                  <button data-testid="match-later" onClick={()=>setMatchModal(null)} className="ps-btn-secondary flex-1">Seguir mirando</button>
                  <button data-testid="match-open-chat" onClick={()=>{const id=matchModal.match_id; setMatchModal(null); nav(`/app/chats/${id}`);}} className="ps-btn-primary flex-1">Abrir chat</button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function ProfileCard({ profile, mode, activities = [], onPass, onLike, resetRef, likeVerbCap = "Me tinca" }) {
  const actById = {};
  const actByName = {};
  activities.forEach((a) => { actById[a.id] = a; actByName[a.name] = a; });
  const resolveAct = (key) => actById[key] || actByName[key] || null;
  const [photoIdx, setPhotoIdx] = useState(0);
  const [gone, setGone] = useState(false); // true after card flies out
  const photo = profile.photos?.[photoIdx];

  const x = useMotionValue(0);
  const rotate = useTransform(x, [-320, 0, 320], [-12, 0, 12]);
  const likeOpacity = useTransform(x, [0, 80, 200], [0, 0.5, 1]);
  const passOpacity = useTransform(x, [-200, -80, 0], [1, 0.5, 0]);
  const thresholdCrossedRef = useRef(false);

  // Expose a resetter so parent can bring the card back (used when plan modal is cancelled).
  useEffect(() => {
    if (resetRef) {
      resetRef.current = () => { setGone(false); fmAnimate(x, 0, { type: "spring", stiffness: 380, damping: 32 }); };
    }
    return () => { if (resetRef) resetRef.current = null; };
    // eslint-disable-next-line
  }, [resetRef]);

  const vibrate = () => { try { navigator.vibrate?.(10); } catch { /* ignore */ } };

  const flyOut = (dir) => {
    const w = typeof window !== "undefined" ? window.innerWidth : 500;
    setGone(true);
    fmAnimate(x, dir * (w * 1.2), { duration: 0.28, ease: [0.5, 0, 0.75, 0] });
  };

  const handleDragEnd = (_, info) => {
    thresholdCrossedRef.current = false;
    const w = typeof window !== "undefined" ? window.innerWidth : 500;
    const threshold = Math.min(140, w * 0.35);
    if (info.offset.x > threshold) {
      vibrate();
      flyOut(1);
      // Open the plan modal AFTER a tiny delay so the fly-out is visible
      setTimeout(() => { onLike(); }, 260);
    } else if (info.offset.x < -threshold) {
      vibrate();
      flyOut(-1);
      setTimeout(() => { onPass(); }, 260);
    } else {
      fmAnimate(x, 0, { type: "spring", stiffness: 400, damping: 32 });
    }
  };

  const handleDrag = (_, info) => {
    // Vibrate ONCE when crossing threshold in either direction
    const w = typeof window !== "undefined" ? window.innerWidth : 500;
    const threshold = Math.min(140, w * 0.35);
    if (!thresholdCrossedRef.current && Math.abs(info.offset.x) > threshold) {
      thresholdCrossedRef.current = true;
      vibrate();
    } else if (thresholdCrossedRef.current && Math.abs(info.offset.x) < threshold * 0.7) {
      thresholdCrossedRef.current = false;
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="profile-card">
      <motion.div
        data-testid="swipe-card"
        drag={gone ? false : "x"}
        dragElastic={0.7}
        dragConstraints={{ left: 0, right: 0 }}
        onDrag={handleDrag}
        onDragEnd={handleDragEnd}
        style={{ x, rotate, touchAction: "pan-y" }}
        className="relative rounded-[32px] overflow-hidden bg-[#1A1C22] border border-white/5 select-none cursor-grab active:cursor-grabbing"
      >
        <div style={{ height: "70vh", maxHeight: 720 }} className="relative">
          {/* Photo */}
          <div className="absolute inset-0">
            {photo ? (
              <img src={fileUrl(photo)} alt={profile.alias} className="w-full h-full object-cover pointer-events-none" draggable={false}/>
            ) : (
              <div className="w-full h-full flex items-center justify-center" style={{ background: "linear-gradient(135deg, #FF6B5E33, #8B5CF633)" }}>
                <div className="w-40 h-40 rounded-full ps-gradient flex items-center justify-center text-6xl font-black font-display">
                  {profile.alias?.slice(0,1).toUpperCase()}
                </div>
              </div>
            )}
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/90 pointer-events-none"/>

          {/* Photo dots (still tappable) */}
          {profile.photos?.length > 1 && (
            <div className="absolute top-3 left-3 right-3 flex gap-1 z-10">
              {profile.photos.map((_, i) => (
                <button key={i} onClick={(e)=>{ e.stopPropagation(); setPhotoIdx(i); }} className="flex-1 h-1 rounded-full" style={{ background: i===photoIdx ? "white" : "rgba(255,255,255,0.35)" }}/>
              ))}
            </div>
          )}

          {/* Stamps */}
          <motion.div
            data-testid="stamp-like"
            style={{ opacity: likeOpacity }}
            className="absolute top-10 left-6 z-20 pointer-events-none"
          >
            <div className="px-4 py-2 rounded-2xl border-4 text-2xl font-black tracking-tight -rotate-[14deg] ps-gradient shadow-2xl">
              ME TINCA
            </div>
          </motion.div>
          <motion.div
            data-testid="stamp-pass"
            style={{ opacity: passOpacity }}
            className="absolute top-10 right-6 z-20 pointer-events-none"
          >
            <div className="px-4 py-2 rounded-2xl border-4 border-white/40 text-2xl font-black tracking-tight rotate-[14deg] bg-white/10 backdrop-blur-sm">
              PASO
            </div>
          </motion.div>

          {/* Info overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-5 space-y-3 pointer-events-none">
            <div className="flex items-end gap-2 flex-wrap">
              <h2 className="font-display text-3xl font-black tracking-tight text-white">
                {profile.alias}<span className="text-white/60 font-medium text-[17px]">, {profile.age}</span>
              </h2>
              {profile.sober_time_badge && (
                <span className="inline-flex items-center gap-1 text-[10.5px] font-bold tracking-tight px-2 py-1 rounded-full" style={{ color: "#4ADE80", background: "rgba(74,222,128,.14)", border: "1px solid rgba(74,222,128,.42)" }}>
                  <Sprout size={11} strokeWidth={1.9}/> {soberLabel(profile.sober_time_badge)}
                </span>
              )}
              {!profile.sober_time_badge && profile.relationship_badge && (
                <span data-testid="discover-relationship-badge" className="inline-flex items-center gap-1 text-[10.5px] font-bold tracking-tight px-2 py-1 rounded-full" style={{ color: "#4ADE80", background: "rgba(74,222,128,.14)", border: "1px solid rgba(74,222,128,.42)" }}>
                  <Sprout size={11} strokeWidth={1.9}/> {relationshipLabel(profile.relationship_badge)}
                </span>
              )}
            </div>
            <p data-testid="profile-comuna" className="text-xs text-[#C7CBD6] flex items-center gap-1">
              <MapPin size={12} strokeWidth={1.9} className="text-white"/> {profile.comuna}
              {profile.distance_km != null && (
                <span data-testid="profile-distance" className="text-[#FF6B5E] font-semibold"> · ~{profile.distance_km >= 50 ? "50+" : profile.distance_km} km</span>
              )}
            </p>
            {profile.prompts?.slice(0,1).map((p, i) => (
              <div key={i} className="rounded-2xl p-3" style={{ background: "rgba(255,255,255,.06)", border: "1px solid rgba(255,255,255,.16)", backdropFilter: "blur(6px)" }}>
                <p className="ps-lab" style={{ marginBottom: 4 }}>
                  <PencilLine size={12} strokeWidth={1.9}/> {p.q?.toUpperCase()}
                </p>
                <p className="text-sm text-white leading-snug">{p.a}</p>
                {profile.favorite_activities?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {profile.favorite_activities.slice(0, 3).map((favKey) => {
                      const act = resolveAct(favKey);
                      const label = act?.name || favKey;
                      return (
                        <span key={favKey} className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full bg-white/5 border border-white/[.16] text-[#C7CBD6]">
                          {act && <Icon name={iconForActivity(act)} size={11} strokeWidth={1.9}/>}
                          {label.split(" ")[0]}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Prompts extra (scroll below card) */}
      <div className="mt-4 space-y-3">
        {profile.bio && (
          <div data-testid="profile-bio" className="ps-bio-card">
            <p className="ps-lab"><PencilLine size={12} strokeWidth={1.9}/> Sobre mí</p>
            <p className="mt-1 text-white leading-relaxed text-[14px]">“{profile.bio}”</p>
          </div>
        )}
        <DiscoverDetailsCard profile={profile}/>
        {profile.prompts?.slice(1).map((p, i) => (
          <div key={i} className="ps-card p-4 grid grid-cols-[40px_1fr] gap-3 items-start">
            <span className="ps-icn"><PencilLine size={20} strokeWidth={1.9}/></span>
            <div>
              <p className="ps-lab"><PencilLine size={12} strokeWidth={1.9}/> {p.q?.toUpperCase()}</p>
              <p className="mt-1 text-sm text-white leading-snug">{p.a}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="flex gap-3 mt-5">
        <button data-testid="pass-btn" onClick={onPass} className="flex-1 py-3.5 rounded-full bg-white/5 border border-white/[.16] font-bold text-white hover:bg-white/10 transition">
          Pasar
        </button>
        <button data-testid="me-tinca-btn" onClick={onLike} className="ps-btn-primary flex-[1.4] py-3.5 flex items-center justify-center gap-2 text-base">
          <Heart size={18} strokeWidth={1.9} fill="white"/> {likeVerbCap}
        </button>
      </div>
    </motion.div>
  );
}

// "BUSCA · DETALLES" card - shows visible optional detail fields
function DiscoverDetailsCard({ profile }) {
  const items = [];
  if (profile.height_cm) items.push({ icon: <Ruler size={14} strokeWidth={1.9}/>, label: "Estatura", value: `${profile.height_cm} cm` });
  if (profile.has_children) {
    const map = { si: "Tiene hijos", no: "Sin hijos" };
    if (map[profile.has_children]) items.push({ icon: <Baby size={14} strokeWidth={1.9}/>, label: "Hijos", value: map[profile.has_children] });
  }
  if (profile.zodiac) items.push({ icon: <Star size={14} strokeWidth={1.9}/>, label: "Signo", value: profile.zodiac });
  if (!items.length) return null;
  return (
    <div data-testid="discover-details" className="ps-card p-4">
      <p className="ps-lab"><Sparkles size={12} strokeWidth={1.9}/> Busca · Detalles</p>
      <ul className="mt-3 space-y-2">
        {items.map((it, i) => (
          <li key={i} className="flex items-center justify-between text-sm">
            <span className="inline-flex items-center gap-2 text-[#C7CBD6]">{it.icon}{it.label}</span>
            <span className="font-semibold text-white">{it.value}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Static preview of the next card (behind the top one) to give a "deck" feel.
function StaticProfilePreview({ profile }) {
  const photo = profile.photos?.[0];
  return (
    <div className="rounded-[32px] overflow-hidden bg-[#1A1C22] border border-white/5" style={{ height: "70vh", maxHeight: 720 }}>
      {photo ? (
        <img src={fileUrl(photo)} alt="" className="w-full h-full object-cover" draggable={false}/>
      ) : (
        <div className="w-full h-full flex items-center justify-center" style={{ background: "linear-gradient(135deg, #FF6B5E33, #8B5CF633)" }}>
          <div className="w-40 h-40 rounded-full ps-gradient flex items-center justify-center text-6xl font-black font-display">
            {profile.alias?.slice(0,1).toUpperCase()}
          </div>
        </div>
      )}
    </div>
  );
}
